import { ExponentialBackoff, handleWhen, retry } from "cockatiel";

import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services";

import type { Numeric, Readable } from "./Readable";
import { StorageManager } from "./StorageManager";

function isNotFoundError(err: unknown): err is DOMException {
  return err instanceof DOMException && err.name === "NotFoundError";
}

function isSyncHandleCreationError(err: unknown) {
  return (
    err instanceof DOMException &&
    (err.name === "NoModificationAllowedError" ||
      err.name === "CreateHandleTimedOutError")
  );
}

interface LocalFileArgs {
  name: string;
  parents?: string[];
  trackLastAccessTime?: boolean;
}

interface ReadArgs {
  offset?: Numeric;
  size?: Numeric;
  signal?: AbortSignal;
}

// This prevents garbage collection of OPFS sync access handle requests before they resolve.
// That allows us to properly dispose of handles that get created after we no longer need them.
const pendingHandles: Set<Promise<unknown>> = new Set();

export class LocalFile implements Readable {
  public lastAccessTime: Date | null = null;
  public readonly name: string;
  public readonly parents: string[];

  private readonly trackLastAccessTime: boolean;

  static fromJSON(value: JsonValue) {
    function isLocalFileArgs(v: unknown): v is LocalFileArgs {
      return (
        typeof v === "object" && v !== null && "name" in v && "parents" in v
      );
    }

    if (!isLocalFileArgs(value)) {
      throw new TypeError(`Expected LocalFile, got ${JSON.stringify(value)}`);
    }
    return new LocalFile(value);
  }

  constructor({
    name,
    parents = [],
    trackLastAccessTime = true,
  }: LocalFileArgs) {
    this.name = name;
    this.parents = parents;
    this.trackLastAccessTime = trackLastAccessTime;
  }

  public async delete() {
    try {
      let container = await navigator.storage.getDirectory();
      for (const dir of this.parents) {
        const child = await container.getDirectoryHandle(dir, {
          create: false,
        });
        container = child;
      }
      await container.removeEntry(this.name);
    } catch (err) {
      if (isNotFoundError(err)) {
        // swallow
        return;
      }
      throw err; // rethrow
    }
  }

  public async exists(expectedSize?: number | bigint, signal?: AbortSignal) {
    try {
      const actualSize = await this.size(signal);
      if (expectedSize !== undefined) {
        return actualSize === BigInt(expectedSize);
      }
      return actualSize > 0;
    } catch {
      return false;
    }
  }

  /**
   * The returned FileSystemSyncAccessHandle MUST be closed once finished with it.
   */
  public getWriteableSyncFileHandle(
    signal?: AbortSignal,
  ): Promise<FileSystemSyncAccessHandle> {
    return this.getSyncAccessHandle({ create: true, writeable: true, signal });
  }

  public id(): string {
    if (this.parents.length === 0) {
      return this.name;
    }
    return `${this.parents.join("#")}#${this.name}`;
  }

  /**
   * Record the time this file was last accessed in a sibling file
   * named `${filename}.last_access_time`.
   *
   * The time is only written to the file if the date portion of the timestamp changes.
   *
   * The last access time is used to determine which files are candidates for GC,
   * implemented as an LRU eviction policy in StorageManager::enforceStorageQuota.
   *
   * Expect this to be done as a "best effort"--any errors encountered are caught and swallowed.
   */
  public memorializeLastAccessTime(signal?: AbortSignal): Promise<void> {
    if (!this.trackLastAccessTime) {
      return Promise.resolve();
    }

    const now = new Date();

    const lastAccessTimePath = new LocalFile({
      name: `${this.name}${StorageManager.LAST_ACCESS_TIME_SUFFIX}`,
      parents: this.parents,
      trackLastAccessTime: false, // don't track last access time of the last access time file itself
    });

    const readLastAccessTime = async (): Promise<Date | null> => {
      try {
        const contents = await lastAccessTimePath.read({
          signal,
        });
        const timestamp = new TextDecoder().decode(contents);
        const lastAccessTime = new Date(timestamp);
        if (Number.isNaN(lastAccessTime.valueOf())) {
          ErrorMonitoringService.captureError(
            `Failed to parse last access timestamp of ${this.toString()}`,
          );
          return null;
        }
        return lastAccessTime;
      } catch (err) {
        // swallow
        if (!isNotFoundError(err)) {
          ErrorMonitoringService.captureError(
            new Error(
              `Failed to parse last access timestamp of ${this.toString()}`,
              {
                cause: err,
              },
            ),
          );
        }
        return null;
      }
    };

    const writeLastAccessTime = async (lastAccessTime: Date) => {
      try {
        const encoder = new TextEncoder();
        const timestamp = encoder.encode(lastAccessTime.toISOString());
        await lastAccessTimePath.write(timestamp, { signal, truncate: true });
        this.lastAccessTime = now;
      } catch (err) {
        // swallow
        ErrorMonitoringService.captureError(
          new Error(
            `Failed to write last access timestamp for ${this.toString()}`,
            {
              cause: err,
            },
          ),
        );
      }
    };

    const isSameDay = (date1: Date, date2: Date): boolean => {
      return date1.toDateString() === date2.toDateString();
    };

    return navigator.locks
      .request(lastAccessTimePath.id(), { signal }, async () => {
        signal?.throwIfAborted();

        if (this.lastAccessTime === null) {
          const storedTime = await readLastAccessTime();
          if (!storedTime) {
            await writeLastAccessTime(now);
          } else {
            this.lastAccessTime = storedTime;
          }
          return;
        }

        if (!isSameDay(this.lastAccessTime, now)) {
          await writeLastAccessTime(now);
        }
      })
      .catch((err) => {
        // swallow
        if (isAbortError(err)) {
          return;
        }

        ErrorMonitoringService.captureError(
          new Error(
            `Failed to memorialize last access time for ${this.toString()}`,
            {
              cause: err,
            },
          ),
        );
      });
  }

  public async read(
    offset?: Numeric,
    size?: Numeric,
    signal?: AbortSignal,
  ): Promise<Uint8Array>;
  public async read(args: ReadArgs): Promise<Uint8Array>;
  public async read(
    ...args: [Numeric?, Numeric?, AbortSignal?] | [ReadArgs]
  ): Promise<Uint8Array> {
    let offset: Numeric | undefined;
    let size: Numeric | undefined;
    let signal: AbortSignal | undefined;
    if (typeof args[0] === "object") {
      const arg = args[0];
      offset = arg.offset ?? 0;
      size = arg.size;
      signal = arg.signal;
    } else {
      offset = args[0] ?? 0;
      size = args[1];
      signal = args[2];
    }

    let fileHandle: FileSystemSyncAccessHandle | undefined;
    let buffer: Uint8Array | undefined;
    try {
      fileHandle = await this.getReadOnlySyncFileHandle(signal);
      signal?.throwIfAborted();

      if (size === undefined) {
        size = fileHandle.getSize();
      }
      buffer = new Uint8Array(Number(size));
      fileHandle.read(buffer, { at: Number(offset) });
    } finally {
      fileHandle?.close();
    }

    if (this.trackLastAccessTime === true && this.lastAccessTime === null) {
      await this.memorializeLastAccessTime(signal);
    }
    return buffer;
  }

  public async size(signal?: AbortSignal): Promise<bigint> {
    let fileHandle;
    try {
      fileHandle = await this.getReadOnlySyncFileHandle(signal);
      return BigInt(fileHandle.getSize());
    } finally {
      fileHandle?.close();
    }
  }

  public toJSON() {
    return {
      name: this.name,
      parents: this.parents,
      trackLastAccessTime: this.trackLastAccessTime,
    };
  }

  public toString(): string {
    return `LocalFile(${this.id()})`;
  }

  public async write(
    bytes: Uint8Array,
    opts?: {
      at?: number;
      signal?: AbortSignal;
      truncate?: boolean;
    },
  ): Promise<void> {
    const { at = 0, signal, truncate = false } = opts ?? {};

    let handle;
    try {
      handle = await this.getWriteableSyncFileHandle(signal);

      if (truncate === true) {
        handle.truncate(0);
      }

      const numBytesWritten = handle.write(bytes, { at });
      if (numBytesWritten !== bytes.byteLength) {
        throw new Error("Failed to cache data, please retry.", {
          cause: `Expected to write ${bytes.byteLength} bytes to ${this.toString()}, wrote ${numBytesWritten}`,
        });
      }
      handle.flush();
    } finally {
      handle?.close();
    }

    if (this.trackLastAccessTime === true && this.lastAccessTime === null) {
      await this.memorializeLastAccessTime(opts?.signal);
    }
  }

  private async getFileHandle({
    create,
    signal,
  }: {
    create: boolean;
    signal?: AbortSignal;
  }) {
    const opfsRoot = await navigator.storage.getDirectory();
    signal?.throwIfAborted();

    let parentDirHandle = opfsRoot;
    const parents = [...this.parents];
    while (parents.length > 0) {
      const parent = parents.shift();
      if (parent === undefined) {
        break;
      }
      parentDirHandle = await parentDirHandle.getDirectoryHandle(parent, {
        create,
      });
      signal?.throwIfAborted();
    }
    const handle = await parentDirHandle.getFileHandle(this.name, {
      create,
    });
    signal?.throwIfAborted();
    return handle;
  }

  /**
   * Will throw a NotFoundError (DOMException) if file is not found.
   * https://developer.mozilla.org/en-US/docs/Web/API/FileSystemDirectoryHandle/getFileHandle#exceptions
   *
   * The returned FileSystemSyncAccessHandle MUST be closed once finished with it.
   */
  private getReadOnlySyncFileHandle(
    signal?: AbortSignal,
  ): Promise<FileSystemSyncAccessHandle> {
    return this.getSyncAccessHandle({
      create: false,
      writeable: false,
      signal,
    });
  }

  /**
   * There can *generally* only be one FileSystemSyncAccessHandle per path. From the docs:
   *  > Creating a FileSystemSyncAccessHandle takes an exclusive lock on the file associated with the file handle.
   *  > This prevents the creation of further FileSystemSyncAccessHandles or FileSystemWritableFileStreams
   *  > for the file until the existing access handle is closed.
   *  https://developer.mozilla.org/en-US/docs/Web/API/FileSystemFileHandle/createSyncAccessHandle
   *
   * While this sets the experimental, non-standard "mode" option of createSyncAccessHandle--
   * which does _not_ take an exclusive lock--support is only implemented in Chromium-based browsers.
   */
  private async getSyncAccessHandle({
    create,
    writeable,
    signal,
  }: {
    create: boolean;
    writeable: boolean;
    signal?: AbortSignal;
  }) {
    const retryGetSyncAccessHandlePolicy = retry(
      handleWhen(isSyncHandleCreationError),
      {
        backoff: new ExponentialBackoff({
          initialDelay: 1, // ms
          maxDelay: 100,
        }),
        maxAttempts: 300,
      },
    );
    try {
      return retryGetSyncAccessHandlePolicy.execute(async (retryContext) => {
        retryContext.signal.throwIfAborted();

        const fileHandle = await this.getFileHandle({
          create,
          signal: retryContext.signal,
        });
        retryContext.signal.throwIfAborted();

        return new Promise<FileSystemSyncAccessHandle>(function (
          resolve,
          reject,
        ) {
          let rejected = false;
          const timeoutAttempt = setTimeout(() => {
            rejected = true;
            reject(
              new DOMException(
                "create sync access handle request timed out",
                "CreateHandleTimedOutError",
              ),
            );
          }, 500);
          const mode = writeable ? "readwrite" : "read-only";
          const innerPromise = fileHandle
            .createSyncAccessHandle({ mode })
            .then((handle) => {
              clearTimeout(timeoutAttempt);
              if (rejected) {
                handle.close();
              }
              resolve(handle);
            })
            .catch((error) => {
              rejected = true;
              clearTimeout(timeoutAttempt);
              reject(error as Error);
            })
            .finally(() => {
              pendingHandles.delete(innerPromise);
            });
          pendingHandles.add(innerPromise);
        });
      }, signal);
    } catch (err) {
      // on give up retrying to get a file system sync access handle
      // remap the exception to something more user facing
      if (isSyncHandleCreationError(err)) {
        throw new Error(
          "Encountered a temporary problem accessing data. Please reload this panel to retry.",
          { cause: err },
        );
      }
      // something else happened, rethrow
      throw err;
    }
  }
}
